import React, { useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { getAppliedJobDetails } from "../helper/My_Helper";

// ===================== HELPER FUNCTIONS =====================

/**
 * Formats a date string to "DD Month YYYY" format
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Gets current date in "DD Month YYYY" format
 * @returns {string} Current date string
 */
const getCurrentDate = () => {
  const date = new Date();
  return `${date.getDate()} ${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
};

/**
 * Extracts candidate name from various data structures
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Full name
 */
const getCandidateName = (candidateData) => {
  if (!candidateData) return "";
  
  const fullName = candidateData?.applicant_form_data?.full_name;
  if (fullName && typeof fullName === "object") {
    return [fullName?.first_name, fullName?.middle_name, fullName?.surname]
      .filter(Boolean)
      .join(" ");
  }
  return candidateData?.name || "";
};

/**
 * Gets father/husband name from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Father/husband name
 */
const getFatherHusbandName = (candidateData) => {
  return candidateData?.applicant_form_data?.father_hushband_name || "";
};

/**
 * Gets formatted gender from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Formatted gender
 */
const getGender = (candidateData) => {
  const gender = candidateData?.applicant_form_data?.gender || "";
  const genderMap = {
    "male": "Male",
    "female": "Female",
    "m": "Male",
    "f": "Female"
  };
  return genderMap[gender?.toLowerCase()] || gender;
};

/**
 * Gets formatted marital status from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Formatted marital status
 */
const getMaritalStatus = (candidateData) => {
  const status = candidateData?.applicant_form_data?.marital_status || "";
  const statusMap = {
    "married": "Married",
    "unmarried": "Unmarried",
    "widow": "Widow",
    "widower": "Widower",
    "divorced": "Divorced",
    "single": "Unmarried"
  };
  return statusMap[status?.toLowerCase()] || status;
};

/**
 * Gets formatted date of birth from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Formatted DOB
 */
const getDateOfBirth = (candidateData) => {
  const dob = candidateData?.applicant_form_data?.dob;
  if (dob) {
    const date = dob.date || "";
    const month = dob.month || "";
    const year = dob.year || "";
    return `${date} ${month} ${year}`.trim();
  }
  return "";
};

/**
 * Gets formatted permanent address from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {string} Formatted address
 */
const getPermanentAddress = (candidateData) => {
  const address = candidateData?.applicant_form_data?.permanent_address;
  if (address) {
    const addr = address.address || "";
    const pincode = address.pincode || "";
    return addr && pincode ? `${addr}, Pincode: ${pincode}` : addr || pincode || "";
  }
  return "";
};

/**
 * Extracts gratuity nominees from various data sources
 * @param {Object} candidateData - The candidate data object
 * @returns {Array} List of nominees
 */
const getGratuityNominees = (candidateData) => {
  if (!candidateData) return [];
  
  // Check direct gratuity nominee details
  let nominees = candidateData?.gratuity_nominee_details || [];
  
  // If no nominees, check in applied_jobs
  if (nominees.length === 0) {
    const appliedJobs = candidateData?.applied_jobs || [];
    for (const job of appliedJobs) {
      if (job.gratuity_nominee_details && job.gratuity_nominee_details.length > 0) {
        return job.gratuity_nominee_details;
      }
    }
  }
  
  return nominees;
};

/**
 * Extracts family members from candidate data
 * @param {Object} candidateData - The candidate data object
 * @returns {Array} List of family members
 */
const getFamilyMembers = (candidateData) => {
  return candidateData?.applicant_form_data?.family_members || [];
};

/**
 * Generates relationship note based on family members
 * @param {Array} familyMembers - List of family members
 * @returns {string} Relationship description
 */
const getRelationshipNote = (familyMembers) => {
  if (!familyMembers || familyMembers.length === 0) return "Family Members";
  
  const hasSpouse = familyMembers.some(m => 
    m.particulars?.toLowerCase() === "spouse" || 
    m.particulars?.toLowerCase() === "husband" ||
    m.particulars?.toLowerCase() === "wife"
  );
  
  const hasChildren = familyMembers.some(m => 
    m.particulars?.toLowerCase() === "son" || 
    m.particulars?.toLowerCase() === "daughter"
  );
  
  const hasParents = familyMembers.some(m => 
    m.particulars?.toLowerCase() === "father" || 
    m.particulars?.toLowerCase() === "mother"
  );
  
  const hasSiblings = familyMembers.some(m => 
    m.particulars?.toLowerCase() === "brother" || 
    m.particulars?.toLowerCase() === "sister"
  );

  const relationships = [];
  if (hasSpouse) relationships.push("Wife");
  if (hasChildren) relationships.push("Children");
  if (hasParents) relationships.push("dependent parents");
  if (hasSiblings) relationships.push("siblings");

  return relationships.length > 0 ? relationships.join("/") : "Family Members";
};

/**
 * Prepares nominee rows for the table
 * @param {Array} nominees - List of nominees
 * @returns {Array} Formatted nominee rows (minimum 5 rows)
 */
const getNomineeRows = (nominees) => {
  let rows = [];
  
  if (nominees && nominees.length > 0) {
    rows = nominees.map(nominee => ({
      name: nominee.name || "",
      address: nominee.address || "",
      relationship: nominee.relationship || "",
      age: nominee.age || "",
      proportion: nominee.proportion || ""
    }));
  }
  
  // Ensure at least 5 rows
  while (rows.length < 5) {
    rows.push({ name: "", address: "", relationship: "", age: "", proportion: "" });
  }
  
  return rows;
};

// ===================== MAIN COMPONENT =====================

const GratuityNominationForm = ({ candidateData = {} }) => {
  const formRef = useRef();

  // ===================== DATA EXTRACTION =====================
  const appliedJob = getAppliedJobDetails(candidateData);
  const nomineeData = useMemo(() => getGratuityNominees(candidateData), [candidateData]);
  const familyMembersData = useMemo(() => getFamilyMembers(candidateData), [candidateData]);
  
  const candidateName = useMemo(() => getCandidateName(candidateData), [candidateData]);
  const fatherHusbandName = useMemo(() => getFatherHusbandName(candidateData), [candidateData]);
  const gender = useMemo(() => getGender(candidateData), [candidateData]);
  const maritalStatus = useMemo(() => getMaritalStatus(candidateData), [candidateData]);
  const dateOfBirth = useMemo(() => getDateOfBirth(candidateData), [candidateData]);
  const permanentAddress = useMemo(() => getPermanentAddress(candidateData), [candidateData]);
  const nomineeRows = useMemo(() => getNomineeRows(nomineeData), [nomineeData]);
  const relationshipNote = useMemo(() => getRelationshipNote(familyMembersData), [familyMembersData]);
  
  const placeOfPosting = candidateData?.annexure_eleven_form_data?.place_of_posting || appliedJob?.proposed_location || appliedJob?.job_location || candidateData?.location || "";
  const doj = candidateData?.annexure_eleven_form_data?.candidate_doj || appliedJob?.onboard_date ||"";
  const signature = candidateData?.applicant_form_data?.signature || "";

  // ===================== PRINT HANDLER =====================

  const printStyles = `
    @page {
      size: A4;
      margin: 10mm 12mm;
    }
    @media print {
      html, body {
        width: 210mm;
        height: 297mm;
        margin: 0;
        padding: 0;
      }
      .no-print {
        display: none !important;
      }
      .page-break {
        page-break-before: always;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .print-content {
        font-size: 11.5px !important;
        line-height: 1.4 !important;
      }
      .print-content p, .print-content .MuiTypography-root {
        font-size: 11.5px !important;
        line-height: 1.4 !important;
      }
      .print-content .MuiTableCell-root {
        padding: 5px 8px !important;
        font-size: 10px !important;
      }
      .print-content .MuiTableRow-root {
        min-height: 28px !important;
      }
    }
  `;

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: printStyles,
    onPrintError: (error) => {
      console.error("Print error:", error);
      alert("There was an error while printing. Please try again.");
    },
  });

  // ===================== LOADING STATE =====================

  if (!candidateData || Object.keys(candidateData).length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  // ===================== RENDER =====================

  return (
    <Box>
      {/* Print Button */}
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ minWidth: 150 }}
        >
          Print Form
        </Button>
      </Box>

      {/* Printable Content */}
      <Box
        ref={formRef}
        className="print-content"
        sx={{
          backgroundColor: "#fff",
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "210mm",
          mx: "auto",
          fontSize: "11.5px",
          lineHeight: 1.4,
          color: "#000",
        }}
      >
        {/* ===================== PAGE 1 ===================== */}
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ position: "relative", mb: 3, minHeight: "55px" }}>
            <Box sx={{ position: "absolute", left: 0, top: 0 }}>
              <img
                src={
                  config?.IMAGE_PATH
                    ? config.IMAGE_PATH + "logo512.png"
                    : "/logo512.png"
                }
                alt="HLFPPT Logo"
                height="42"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo512.png";
                }}
              />
            </Box>
            <Typography
              align="center"
              sx={{ fontSize: "16px", fontWeight: "bold", pt: 1 }}
            >
              APPENDIX 1
            </Typography>
            <Typography
              align="center"
              sx={{ fontSize: "15px", fontWeight: "bold", mt: 0.5 }}
            >
              FORM OF APPOINTMENT OF BENEFICIARY
            </Typography>
            <Typography align="center" sx={{ fontSize: "14px", mt: 0.3 }}>
              (NOMINATION)
            </Typography>
          </Box>

          <Typography sx={{ fontSize: "12.5px", mb: 1.5 }}>The,</Typography>
          <Typography sx={{ fontSize: "12.5px", mb: 2.5, fontWeight: "bold" }}>
            Hindustan Latex Family Planning Promotion Trust Employees Group
            Gratuity Assurance Scheme
          </Typography>

          <Typography sx={{ fontSize: "12.5px", mb: 2.5 }}>Dear Sir,</Typography>

          {/* First Paragraph - Line by Line */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "12.5px", textAlign: "justify", lineHeight: 2, mb: 1 }}>
              I{" "}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  minWidth: "320px",
                  borderBottom: "2px solid #000",
                  px: 1.5,
                  fontWeight: "medium",
                  pb: 0.5,
                  mx: 0.5,
                }}
              >
                {candidateName}
              </Box>{" "}
              a member of Hindustan Latex Family Planning Promotion Trust Employees Group Gratuity Assurance Scheme hereby agree to abide by the Rules of the said Scheme.
            </Typography>
            <Typography sx={{ fontSize: "12.5px", textAlign: "justify", lineHeight: 2, mb: 1 }}>
              I do also hereby appoint in terms of Rule 18 of the Rules the Beneficiary/ies Nominee/s mentioned hereunder to receive the benefits, payable under the scheme, in the event of my death before the amount become payable has not been paid.
            </Typography>
          </Box>

          <Typography sx={{ fontSize: "12.5px", mb: 3, textAlign: "justify", lineHeight: 2 }}>
            I hereby direct that the benefits under the scheme, payable in respect of me, shall be paid to the said Beneficiary/ies Nominee/s in proportion indicated against their respective name(s) as given below:
          </Typography>

          {/* Nominee Table */}
          <TableContainer sx={{ mb: 3.5, border: "2px solid #000" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 1.5,
                      width: "8%",
                      textAlign: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    Sr. No.
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 1.5,
                      width: "35%",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    Name in full with full address of nominee(s) / Beneficiary(ies)
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 1.5,
                      width: "18%",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    Relationship with the member (Employee)
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 1.5,
                      width: "15%",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    Age of nominee(s) / Beneficiary(ies)
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 1.5,
                      width: "24%",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    Proportion by which the gratuity will be shared by Each Nominee Beneficiary
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nomineeRows.map((nominee, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        border: "1px solid #000",
                        p: 2.5,
                        textAlign: "center",
                        fontSize: "11px",
                        height: "42px",
                      }}
                    >
                      {index + 1}.
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: "1px solid #000", 
                        p: 2.5, 
                        height: "42px",
                        verticalAlign: "middle",
                      }}
                    >
                      {nominee.name && nominee.address ? 
                        `${nominee.name}, ${nominee.address}` : 
                        nominee.name || nominee.address || ""
                      }
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: "1px solid #000", 
                        p: 2.5, 
                        height: "42px",
                        verticalAlign: "middle",
                      }}
                    >
                      {nominee.relationship || ""}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: "1px solid #000", 
                        p: 2.5, 
                        height: "42px",
                        verticalAlign: "middle",
                      }}
                    >
                      {nominee.age || ""}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: "1px solid #000", 
                        p: 2.5, 
                        height: "42px",
                        verticalAlign: "middle",
                      }}
                    >
                      {nominee.proportion || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Remaining paragraphs */}
          <Typography sx={{ fontSize: "12.5px", mb: 2.5, textAlign: "justify", lineHeight: 2 }}>
            I hereby certify that the person(s) mentioned hereinabove is/are my {relationshipNote}.
          </Typography>

          <Typography sx={{ fontSize: "12.5px", mb: 2.5, textAlign: "justify", lineHeight: 2 }}>
            I hereby declare that I have no family and should I acquire family hereafter the appointment of Beneficiary / Nominee should be deemed as cancelled.
          </Typography>

          <Typography sx={{ fontSize: "12.5px", mb: 2.5, textAlign: "justify", lineHeight: 2 }}>
            My father / mother / parents / sister('s) minor brother(s) is/are not dependent on me.
          </Typography>

          <Typography sx={{ fontSize: "12.5px", mb: 3, textAlign: "justify", lineHeight: 2 }}>
            My husband's father / parents is/are not dependent on me.
          </Typography>

          <Typography sx={{ fontSize: "12.5px", textAlign: "justify", lineHeight: 2 }}>
            I also declare that this appointment of Beneficiary(ies)/Nominee(s) made herein shall have the effect of my revoking the appointment of Beneficiary(ies)/Nominee(s) made by the earlier.
          </Typography>
        </Box>

        {/* ===================== PAGE 2 ===================== */}
        <Box className="page-break" sx={{ p: 2.5 }}>
          <Typography
            sx={{ fontSize: "12px", fontWeight: "bold", mb: 1.5 }}
          >
            I give below the particulars about myself:
          </Typography>

          {[
            { label: "1. Full Name", value: candidateName },
            { label: "2. Sex", value: gender },
            { label: "3. Religion", value: "" },
            { label: "4. Father's Name", value: fatherHusbandName },
            {
              label: "5. Husband's Name",
              value: gender === "Female" ? fatherHusbandName : "",
              note: "(For married women only)",
            },
            {
              label: "6. Marital Status",
              value: maritalStatus,
              note: "(Whether married, unmarried, widow or widower)",
            },
            { label: "7. Date of Birth", value: dateOfBirth },
            { label: "8. Permanent Address", value: permanentAddress, multiline: true },
          ].map((item, idx) => (
            <Box key={idx} sx={{ mb: 1.2 }}>
              <Typography sx={{ fontSize: "11.5px", mb: 0.3 }}>
                {item.label}:
                {item.note && (
                  <Typography
                    component="span"
                    sx={{ fontSize: "10.5px", fontStyle: "italic", ml: 0.8 }}
                  >
                    {item.note}
                  </Typography>
                )}
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: item.multiline ? "45px" : "24px",
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item.value && (
                  <Typography sx={{ fontSize: "11.5px" }}>{item.value}</Typography>
                )}
              </Box>
            </Box>
          ))}

          {/* Signature section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography sx={{ fontSize: "11.5px", mb: 0.8 }}>
              Signed at: ____________________ this {formatDate(doj) || getCurrentDate()}
            </Typography>

            <Typography
              align="right"
              sx={{ fontSize: "11.5px", mb: 0.5, mt: 2 }}
            >
              (Signature of Member/Employee)
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                width: "180px",
                height: "35px",
                ml: "auto",
                mb: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {signature && (
                <img
                  src={`${config?.IMAGE_PATH || ""}${signature}`}
                  alt="candidate-signature"
                  style={{
                    maxHeight: "22px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              )}
            </Box>
          </Box>

          <Typography sx={{ fontSize: "11.5px", fontWeight: "bold", mb: 1 }}>
            Two witness to the signature
          </Typography>

          <TableContainer sx={{ mb: 2.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 0.8,
                      width: "8%",
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 0.8,
                      width: "30%",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 0.8,
                      width: "40%",
                    }}
                  >
                    Address
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      fontSize: "10px",
                      p: 0.8,
                      width: "22%",
                    }}
                  >
                    Signature
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2].map((num) => (
                  <TableRow key={num} sx={{ minHeight: "35px" }}>
                    <TableCell
                      sx={{
                        border: "1px solid #000",
                        p: 1.3,
                        textAlign: "center",
                        fontSize: "10.5px",
                      }}
                    >
                      {num}.
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1.3, minHeight: "35px" }} />
                    <TableCell sx={{ border: "1px solid #000", p: 1.3, minHeight: "35px" }} />
                    <TableCell sx={{ border: "1px solid #000", p: 1.3, minHeight: "35px" }} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Certification */}
          <Typography sx={{ fontSize: "11.5px", mb: 1.5, textAlign: "justify", lineHeight: 1.5 }}>
            Certified that the above appointment of Beneficiary Nominee has been
            signed by Shri/Shrimati <Box component="span" sx={{ borderBottom: "1px solid #000", px: 2 }}>{candidateName}</Box> before me after he/she
            has read the entries, the entries have been read to him/her by me and
            that the said appointment of Beneficiary/Nominee is recorded under
            the scheme on ____________________.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "11.5px" }}>Place:</Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "25px",
                  mt: 0.5,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                }}
              >
                <Typography sx={{ fontSize: "11.5px" }}>{placeOfPosting}</Typography>
              </Box>
              <Typography sx={{ fontSize: "11.5px" }}>Date:</Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "25px",
                  mt: 0.5,
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                }}
              >
                <Typography sx={{ fontSize: "11.5px" }}>{formatDate(doj)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "40px",
                  mb: 0.5,
                }}
              />
              <Typography align="center" sx={{ fontSize: "11px" }}>
                Signature
              </Typography>
              <Typography
                align="center"
                sx={{ fontSize: "10.5px", mt: 0.8 }}
              >
                Group Gratuity Scheme
              </Typography>
            </Grid>
          </Grid>

          {/* Notes */}
          <Box sx={{ mt: 2.5, borderTop: "1px solid #999", pt: 1.5 }}>
            <Typography
              sx={{ fontSize: "10.5px", fontWeight: "bold", mb: 0.8 }}
            >
              NOTE:
            </Typography>
            <Typography
              sx={{ fontSize: "10px", mb: 0.8, textAlign: "justify", lineHeight: 1.4 }}
            >
              Where an Employee Member has a family at the time of appointing a
              Beneficiary/Nominee, the Nomination should be made in favour of
              Members of his family only. Any Nomination made by such Employee
              in favour of any other persons not belonging to his family shall be
              invalid.
            </Typography>
            <Typography
              sx={{ fontSize: "10px", mb: 0.8, textAlign: "justify", lineHeight: 1.4 }}
            >
              An Appointment of Beneficiary/Nominee made by the member may be
              changed at any time, after giving a written notice to the Trustees
              of his intention to do so. If the Nominee predeceases the member
              (Employee), the interest of the Nominee shall revert to the Member
              (Employee) or his estate.
            </Typography>
            <Typography
              sx={{ fontSize: "10px", mb: 0.8, textAlign: "justify", lineHeight: 1.4 }}
            >
              The appointment of Beneficiary/Nominee or any change thereof made
              from time to time shall take effect to the extent it is valid on
              the date on which it is received by the Trustees.
            </Typography>
            <Typography sx={{ fontSize: "10px", textAlign: "justify", lineHeight: 1.4 }}>
              For the purpose of the Scheme, "Family" means Member's
              (Employee's) spouse, legitimate children/Step children, parents,
              sisters and minor brothers dependent upon him.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GratuityNominationForm;